/**
 * One approval, the same everywhere (19 Oct, UI review findings 4, 5 and 8).
 *
 * WHY: approvers arrive from the Ivanti email (the case page, `#item-n`), from
 * Teams and the reminders (`/approvals?case=`) and from My tickets, and met
 * two designs: a one-click Approve on My approvals, an always-open reason box
 * and small buttons on the case page. The always-open box invited a reason
 * and then Approve by habit. Now both pages use this card:
 *
 * - a header line: the scope (system and area, for which entity), then who
 *   it is for (job title, site), the start date and the manager;
 * - "You approve because …" as body text, not a muted caption;
 * - Approve is one click and names what it approves ("Approve 365FO –
 *   Sales"); Reject opens a required comment first;
 * - one line on what happens next for each, true to what this tenant does:
 *   "by email" only when Ivanti's rejection rule (N5) exists
 *   (`rejection_email`, `ACCESS_REJECTION_EMAIL`).
 *
 * On the case page (`compact`) the facts list and the "Open access case"
 * link are left out: the page around it already shows them. Opened from a
 * link (`focused`), the card scrolls into view and its heading takes focus,
 * so a screen reader announces it.
 */

import { Badge, Button, Card, Field, Textarea, Typography } from "@norican/react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Link } from "react-router";

import { ErrorState, useDateFormatter } from "@/components/common";
import { useFeedbackToast } from "@/features/tickets";
import { useT } from "@/i18n";
import type { AccessApprovalNeed, MyApproval } from "@/lib/api";

import { useDecideApproval } from "../api";
import { withLocalDates } from "../lib/access";
import { dueState, dueTone } from "../lib/my-approvals";
import { useNeedDetail, useNeedStatus } from "./need-words";
import { StatusBadge } from "./status-badge";

export interface ApprovalCardProps {
  card: MyApproval;
  /** Opened from a link to this case: an accent edge. */
  highlighted?: boolean;
  /** Opened from a link to this approval: scrolled to, its heading focused. */
  focused?: boolean;
  /** On the case page: no facts list and no "Open access case" link. */
  compact?: boolean;
  /** Ivanti emails rejections in this tenant (rule N5). */
  rejectionEmail?: boolean;
}

export function ApprovalCard({
  card,
  highlighted = false,
  focused = false,
  compact = false,
  rejectionEmail = false,
}: ApprovalCardProps) {
  const { t } = useT("tickets");
  const { formatDate, formatRelative } = useDateFormatter();
  const status = useNeedStatus();
  const detail = useNeedDetail();
  const heading = useRef<HTMLElement>(null);
  const need = card.need;
  const waitingNow = need.can_decide === true;
  const due = waitingNow ? dueState(need) : null;
  const roles = (card.roles ?? [])
    .map((role) => (role.level ? `${role.label} (${role.level})` : role.label))
    .join(", ");
  const asked = need.waiting_since ?? card.requested_at ?? null;
  const companies = (card.companies ?? []).join(", ");
  const scope = companies
    ? t("access.myApprovals.scopeFor", { what: need.what, entity: companies })
    : need.what;
  const person = [card.person_name, card.job_title, card.site]
    .filter((part): part is string => typeof part === "string" && part.trim() !== "")
    .join(", ");
  const personLine = [
    person,
    card.start_date
      ? t("access.myApprovals.startsOn", {
          date: formatDate(new Date(`${card.start_date}T12:00:00`)),
        })
      : null,
    card.manager ? t("access.manager", { name: card.manager }) : null,
  ]
    .filter((part): part is string => part !== null && part !== "")
    .join(" · ");
  const decidedLine = waitingNow ? null : detail(need);

  useEffect(() => {
    if (!focused || heading.current === null) {
      return;
    }
    heading.current.scrollIntoView({ block: "start" });
    heading.current.focus({ preventScroll: true });
  }, [focused]);

  return (
    <Card
      variant="outline"
      padding="md"
      accentTone={due === "overdue" ? "danger" : highlighted || focused ? "accent" : undefined}
    >
      <div className="flex min-w-0 flex-col gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="flex min-w-0 flex-wrap items-center gap-2">
            <Typography
              ref={heading}
              as="h3"
              variant="heading-6"
              tabIndex={-1}
              data-approval-heading=""
              className="min-w-0 scroll-mt-4 break-words focus:outline-none"
            >
              {scope}
              {card.action ? ` · ${t(`access.action.${card.action}`)}` : ""}
            </Typography>
            <span className="ml-auto flex flex-wrap gap-2">
              <Badge tone="neutral" variant="subtle" size="sm">
                {t(`access.type.${card.case_type}`)}
              </Badge>
              {due !== null ? (
                <Badge
                  tone={dueTone(due)}
                  variant={due === "overdue" ? "solid" : "subtle"}
                  size="sm"
                >
                  {due === "overdue"
                    ? t("access.myApprovals.overdue")
                    : t("access.myApprovals.due", { when: formatRelative(need.due_at ?? "") })}
                </Badge>
              ) : !waitingNow ? (
                <StatusBadge domain="need" status={need.state} size="sm">
                  {status(need)}
                </StatusBadge>
              ) : null}
            </span>
          </span>
          {personLine ? (
            <Typography as="p" variant="body" className="m-0 min-w-0 font-medium break-words">
              {personLine}
            </Typography>
          ) : null}
        </div>
        <ApproverReason need={need} mine={waitingNow && !need.on_behalf} />
        {compact ? null : (
          <dl className="m-0 grid min-w-0 grid-cols-1 gap-x-4 gap-y-1 text-sm sm:grid-cols-[max-content_1fr]">
            <Fact label={t("access.myApprovals.referenceUser")}>
              {card.reference_user ?? t("access.myApprovals.referenceNone")}
            </Fact>
            {roles ? (
              <Fact label={t("access.myApprovals.roles")}>
                {card.picked_by
                  ? t("access.myApprovals.rolesBy", { roles, name: card.picked_by })
                  : roles}
              </Fact>
            ) : null}
            {card.why ? (
              <Fact label={t("access.myApprovals.why")}>
                <span className="whitespace-pre-line">{card.why}</span>
              </Fact>
            ) : null}
            <Fact label={t("access.myApprovals.askedBy")}>
              {t("access.myApprovals.askedLine", {
                name: card.requested_by ?? "—",
                when: asked ? formatRelative(asked) : "—",
              })}
            </Fact>
            {waitingNow && (need.reminders_sent ?? 0) > 0 ? (
              <Fact label={t("access.myApprovals.remindersLabel")}>
                {t("access.myApprovals.reminders", { n: need.reminders_sent ?? 0 })}
              </Fact>
            ) : null}
          </dl>
        )}
        {decidedLine ? (
          <Typography as="p" variant="body-sm" className="m-0 break-words">
            {decidedLine}
          </Typography>
        ) : null}
        {!waitingNow && need.reason ? (
          <Typography as="p" variant="body-sm" className="m-0 break-words">
            {t("access.myApprovals.yourReason", { reason: need.reason })}
          </Typography>
        ) : null}
        {waitingNow ? (
          <DecideApproval
            caseId={card.case_id}
            n={card.n}
            need={need}
            team={card.team ?? null}
            rejectionEmail={rejectionEmail}
          />
        ) : null}
        {compact ? null : (
          <Link
            to={`${card.case_path}#item-${card.n}`}
            className="text-accent-fg-subtle w-fit text-sm underline underline-offset-2"
          >
            {t("access.myApprovals.openCase", { number: card.case_number })}
          </Link>
        )}
      </div>
    </Card>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-fg-muted">{label}</dt>
      <dd className="m-0 min-w-0 break-words">{children}</dd>
    </>
  );
}

/**
 * "You approve because: 365FO Sales area owner, USL" (finding 5): the
 * strongest trust cue, as body text. Standing in for someone (a test
 * approver, an admin on their behalf) says so instead.
 */
function ApproverReason({ need, mine }: { need: AccessApprovalNeed; mine: boolean }) {
  const { t } = useT("tickets");
  const { formatDate } = useDateFormatter();
  const rule = withLocalDates(need.why, formatDate);
  const name = need.approver ?? t("access.approvals.nobodyNamed");
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <Typography as="p" variant="body-sm" className="m-0 min-w-0 break-words">
        {mine && !need.as_test_approver ? (
          <>
            <strong>{t("access.myApprovals.becauseLabel")}</strong> {rule}
          </>
        ) : need.approver ? (
          t("access.approvals.approverLine", { name: need.approver, rule })
        ) : (
          t("access.approvals.ruleLine", { rule })
        )}
      </Typography>
      {/* 28 Oct (Rusty): a test approver (test systems only) gets one small marker. */}
      {need.as_test_approver ? (
        <Badge tone="neutral" variant="subtle" size="xs" className="self-start">
          {t("access.completed.test.badge")}
        </Badge>
      ) : null}
      {need.on_behalf ? (
        <Typography as="p" variant="body-sm" className="m-0 break-words">
          {t("access.approvals.onBehalfHint", { name })}
        </Typography>
      ) : null}
    </div>
  );
}

export interface DecideApprovalProps {
  caseId: string;
  n: number;
  need: AccessApprovalNeed;
  /** The team that gives the access once approved, for "what happens next". */
  team: string | null;
  rejectionEmail: boolean;
}

/** One click to approve, named; Reject opens a required comment first. */
export function DecideApproval({ caseId, n, need, team, rejectionEmail }: DecideApprovalProps) {
  const { t } = useT("tickets");
  const toast = useFeedbackToast();
  const decide = useDecideApproval(caseId, n);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [missing, setMissing] = useState(false);
  const who = team ?? t("access.decide.teamFallback");
  const rejectHint = t(
    rejectionEmail ? "access.decide.rejectHintEmail" : "access.decide.rejectHint",
    {
      team: who,
    },
  );

  const send = (decision: "approve" | "reject") => {
    const text = reason.trim();
    if (decision === "reject" && text === "") {
      setMissing(true);
      return;
    }
    setMissing(false);
    decide
      .mutateAsync({ needId: need.need_id, body: { decision, reason: text === "" ? null : text } })
      .then(
        () =>
          toast.success(
            t(
              decision === "approve"
                ? "access.approvals.approvedToast"
                : "access.approvals.rejectedToast",
            ),
          ),
        () => undefined,
      );
  };

  return (
    <div className="flex flex-col gap-2">
      {decide.error ? <ErrorState variant="inline" error={decide.error} /> : null}
      {rejecting ? (
        <div className="border-border-subtle flex flex-col gap-2 rounded-md border p-3">
          <Field
            name={`reject-${caseId}-${n}-${need.need_id}`}
            label={t("access.myApprovals.rejectLabel")}
            hint={rejectHint}
            error={missing ? t("access.approvals.reasonRequired") : undefined}
          >
            <Textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={2}
              autoResize
              maxLength={1000}
              autoFocus
            />
          </Field>
          <span className="flex flex-wrap gap-2">
            <Button
              size="md"
              tone="danger"
              disabled={decide.isPending}
              onClick={() => send("reject")}
              className="max-sm:flex-1"
            >
              {t("access.decide.rejectNamed", { what: need.what })}
            </Button>
            <Button
              size="md"
              variant="ghost"
              tone="neutral"
              disabled={decide.isPending}
              onClick={() => {
                setRejecting(false);
                setMissing(false);
              }}
              className="max-sm:flex-1"
            >
              {t("access.myApprovals.cancel")}
            </Button>
          </span>
        </div>
      ) : (
        <span className="flex flex-wrap gap-2">
          <Button
            size="md"
            disabled={decide.isPending}
            onClick={() => send("approve")}
            className="max-sm:flex-1"
          >
            {t("access.decide.approveNamed", { what: need.what })}
          </Button>
          <Button
            size="md"
            variant="outline"
            tone="danger"
            disabled={decide.isPending}
            onClick={() => setRejecting(true)}
            className="max-sm:flex-1"
          >
            {t("access.myApprovals.rejectOpen")}
          </Button>
        </span>
      )}
      {rejecting ? null : (
        <ul className="text-fg m-0 flex list-none flex-col gap-0.5 p-0 text-sm">
          <li>{t("access.decide.nextApprove", { team: who })}</li>
          <li>{t("access.decide.nextReject", { hint: rejectHint })}</li>
        </ul>
      )}
    </div>
  );
}
